import { z } from 'zod';
import { PersonRef } from './people.js';
import { ProjectKind } from './projects.js';
import { TimeEntry } from './work.js';
/**
 * Timesheets, retainer burn-down and the monthly client report (docs/adr/0042-timesheets-and-retainers.md).
 * Dates are calendar dates (YYYY-MM-DD) and months 'YYYY-MM'. A range's `end` is exclusive; `last` is inclusive.
 */
export const Month = z.string().regex(/^\d{4}-(0[1-9]|1[0-2])$/, 'Use a month like 2026-10');
export const TimesheetQuery = z.object({
    /** Any day in the week; defaults to today in the viewer's time zone. */
    week: z.iso.date().optional(),
    /** Whose sheet. Members may only ask for their own (anyone else's answers 404). */
    userId: z.uuid().optional(),
    projectId: z.uuid().optional(),
});
export const TimesheetRow = z.object({
    key: z.string(),
    /** Null for a task without a project (projectName is then empty). */
    projectId: z.uuid().nullable(),
    projectName: z.string(),
    clientName: z.string(),
    workItemId: z.uuid().nullable(),
    /** "PRJ-12", when the row is a task. */
    workItemKey: z.string().nullable(),
    /** The task's title, or the project's name for time logged on the project. */
    title: z.string(),
    /** Minutes per day, aligned with `days`. */
    minutes: z.array(z.number().int()).length(7),
    totalMinutes: z.number().int(),
});
export const Timesheet = z.object({
    person: PersonRef,
    /** The viewer's today, in their time zone. */
    today: z.iso.date(),
    weekStart: z.iso.date(),
    weekLast: z.iso.date(),
    days: z.array(z.iso.date()).length(7),
    rows: z.array(TimesheetRow),
    dayTotals: z.array(z.number().int()).length(7),
    totalMinutes: z.number().int(),
    /** The person's weekly capacity, in hours (setting `time.weeklyCapacityHours`). */
    capacityHours: z.number(),
    /** The week's entries, for editing in place. */
    entries: z.array(TimeEntry),
    /** People whose sheets the viewer may open: everyone for owners and admins, empty for members. */
    people: z.array(PersonRef),
    /** The viewer is looking at their own sheet and may add to it. */
    canAdd: z.boolean(),
});
export const UpdateTimeEntryInput = z
    .object({
    minutes: z.number().int().min(1).max(24 * 60).optional(),
    date: z.iso.date().optional(),
    note: z.string().max(500).optional(),
    billable: z.boolean().optional(),
})
    .refine((v) => v.minutes !== undefined || v.date !== undefined || v.note !== undefined || v.billable !== undefined, { message: 'Change the time, date, note or billable' });
export const RetainerState = z.enum(['none', 'under', 'near', 'over']);
export const RetainerUsage = z.object({
    /** Null: the project has no monthly retainer. */
    retainerMinutes: z.number().int().nullable(),
    usedMinutes: z.number().int(),
    remainingMinutes: z.number().int().nullable(),
    overMinutes: z.number().int(),
    percent: z.number().int().nullable(),
    state: RetainerState,
});
export const RetainerWeek = z.object({
    start: z.iso.date(),
    last: z.iso.date(),
    minutes: z.number().int(),
    cumulativeMinutes: z.number().int(),
    targetMinutes: z.number().int().nullable(),
    /** Starts after today: nothing logged in it yet. */
    future: z.boolean(),
});
export const RetainerMonth = RetainerUsage.extend({ month: Month });
export const RetainerQuery = z.object({ month: Month.optional() });
export const RetainerBurn = z.object({
    projectId: z.uuid(),
    projectName: z.string(),
    kind: ProjectKind,
    month: Month,
    monthStart: z.iso.date(),
    monthEnd: z.iso.date(),
    /** The month shown is the current one (the viewer's today falls in it). */
    current: z.boolean(),
    /** This month against the monthly retainer (kind `retainer`); `state: 'none'` otherwise. */
    usage: RetainerUsage,
    weeks: z.array(RetainerWeek),
    /** The six months before, most recent first. */
    previous: z.array(RetainerMonth),
    /** Kinds `project` and `support`: approved hours for the whole project against everything logged so far. */
    approved: z.object({ minutes: z.number().int(), usedMinutes: z.number().int() }).nullable(),
});
export const TimeReportQuery = z.object({ month: Month.optional() });
export const TimeReportProject = RetainerUsage.extend({
    id: z.uuid(),
    name: z.string(),
    kind: ProjectKind,
    /** Shown in the client portal (setting `time.clientReports`). */
    sharedWithClient: z.boolean(),
});
export const TimeReport = z.object({
    kind: z.enum(['project', 'client']),
    id: z.uuid(),
    name: z.string(),
    clientId: z.uuid(),
    clientName: z.string(),
    month: Month,
    monthStart: z.iso.date(),
    monthEnd: z.iso.date(),
    totalMinutes: z.number().int(),
    projects: z.array(TimeReportProject),
    byTask: z.array(z.object({
        workItemId: z.uuid().nullable(),
        workItemKey: z.string().nullable(),
        title: z.string(),
        projectName: z.string(),
        /** The client sees this task's title in the portal report. */
        clientVisible: z.boolean(),
        minutes: z.number().int(),
    })),
    byPerson: z.array(z.object({ person: PersonRef, minutes: z.number().int() })),
    /** The workspace emails clients a link to last month's report on the 1st (setting `time.monthlyReportEmail`). */
    monthlyEmail: z.boolean(),
});
export const PortalTimeReport = z.object({
    clientName: z.string(),
    month: Month,
    monthStart: z.iso.date(),
    monthEnd: z.iso.date(),
    current: z.boolean(),
    totalMinutes: z.number().int(),
    projects: z.array(RetainerUsage.extend({
        name: z.string(),
        weeks: z.array(RetainerWeek),
        previous: z.array(RetainerMonth),
    })),
    /** Client-visible tasks by title; everything else is one "Other work" line (title null). */
    byTask: z.array(z.object({ title: z.string().nullable(), projectName: z.string(), minutes: z.number().int() })),
    /** First names only, as the portal names assignees. */
    byPerson: z.array(z.object({ name: z.string(), minutes: z.number().int() })),
});
//# sourceMappingURL=timesheets.js.map